// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import { getErrorHelperText } from '../../../../utils/Utils';

const getAssociationSelectOptionLabel = (option) => option?.entity_alias;
const getAssociationSelectGroupBy = (option) => option?.type;

// Custom filter to search on entity_alias and unique_id
const associationFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) =>
    `${option?.entity_alias || ''} ${option?.unique_id || ''}`,
});

const renderAssociationOption = (props, option) => {
  const { key: _key, ...restProps } = props;
  return (
    <Box component="li" key={option?.entity_id} {...restProps}>
      <Typography variant="body1">
        {getAssociationSelectOptionLabel(option)}&nbsp;
        {option?.unique_id && (
          <Typography variant="caption" component="span">
            ({option?.unique_id})
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

/**
 * Association Select field component for selecting model associations
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current selected association id
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance
 * @param {Object} props.formData - Complete form data for API payload
 * @returns {React.ReactElement} The AssociationFieldComponent
 */
const AssociationFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  attribute,
  required = false,
  entityDetailsFormikForm,
  formData = EMPTY_OBJECT,
  entityData = EMPTY_OBJECT,
  loader,
}) => {
  const { getAssociationAttributesData } = useAttribute();
  const [associationOptions, setAssociationOptions] = useState(EMPTY_ARRAY);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const uniqueFilteredAssociation = useMemo(() => {
    const seen = new Set();
    const deduped = (associationOptions || []).filter((item) => {
      let key = '';

      if (item.entity_id != null) {
        key = `association:${item.entity_id}`;
      } else {
        return null;
      }

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    return deduped.sort((a, b) =>
      getAssociationSelectOptionLabel(a).localeCompare(
        getAssociationSelectOptionLabel(b),
        undefined,
        { sensitivity: 'base' }
      )
    );
  }, [associationOptions]);

  const selectedAssociation = useMemo(() => {
    return uniqueFilteredAssociation.find((association) => {
      const comparisonValue =
        typeof value === 'object' ? value?.entity_id : value;

      return association?.entity_id === comparisonValue;
    });
  }, [uniqueFilteredAssociation, value]);

  const fetchAssociationData = useCallback(() => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const payload = {
      source_attribute_id: parseInt(attribute?.id),
      form_data: formData,
      entity_id: entityData?.entity_id,
      entity_type: entityData?.entity_type,
    };

    getAssociationAttributesData(payload, loader)
      .then((res) => {
        if (res?.data) {
          setAssociationOptions(res.data);
        } else {
          setAssociationOptions(EMPTY_ARRAY);
          // Clear selected value if API fails or returns empty
          if (value) {
            onChange(null);
          }
        }
      })
      .catch(() => {
        setAssociationOptions(EMPTY_ARRAY);
        // Clear selected value on API error
        if (value) {
          onChange(null);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    attribute?.id,
    formData,
    getAssociationAttributesData,
    isLoading,
    value,
    onChange,
    entityData,
  ]);

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownOpen(true);
    fetchAssociationData();
  }, [fetchAssociationData]);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, [setIsDropdownOpen]);

  const handleBlur = useCallback(() => {
    entityDetailsFormikForm?.setFieldTouched?.(attribute?.id, true, true);
  }, [entityDetailsFormikForm, attribute?.id]);

  const renderAssociationSelectTextField = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        required={required}
        placeholder="Select association"
        InputProps={{
          ...params.InputProps,
          placeholder: 'Select association',
        }}
        InputLabelProps={{
          shrink: true,
        }}
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors?.[attribute?.id],
            entityDetailsFormikForm?.touched?.[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        )}
      />
    ),
    [entityDetailsFormikForm, attribute, label, titleLabel]
  );

  const handleAssociationChange = useCallback(
    (_e, newAssociationValue) => {
      if (newAssociationValue) {
        onChange(newAssociationValue);
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  useEffect(() => {
    // Fetch association data when component mounts
    fetchAssociationData();
  }, []);

  const isOptionEqualToValue = useCallback((option, val) => {
    return option?.entity_id === val?.entity_id;
  }, []);

  return (
    <Autocomplete
      label={label}
      title={titleLabel}
      value={selectedAssociation || value || null}
      options={uniqueFilteredAssociation || []}
      popupIcon={<DropdownArrow />}
      getOptionLabel={getAssociationSelectOptionLabel}
      groupBy={getAssociationSelectGroupBy}
      renderInput={renderAssociationSelectTextField}
      onChange={handleAssociationChange}
      renderOption={renderAssociationOption}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      onBlur={handleBlur}
      open={isDropdownOpen}
      loading={isLoading}
      loadingText="Loading associations..."
      filterOptions={associationFilterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
};

AssociationFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
    association_template: PropTypes.shape({
      template_name: PropTypes.string,
      template_type: PropTypes.string,
    }),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.oneOfType([PropTypes.array]).isRequired,
  formData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  entityData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loader: PropTypes.bool,
};

export default AssociationFieldComponent;
